/**
 * Clientes y preventa.
 *
 * The client file exists for two reasons. The requirements were explicit about the first: the
 * same sack of rice does not cost every buyer the same, and attaching a price list to a client
 * is what turns "whatever we agreed last time" into something the POS can apply without anyone
 * remembering. The second came later: fiado needs somebody to owe it, with a limit and a
 * balance.
 *
 * Orders live here too, because an order is a fact about a client, not about inventory: the stock
 * one sets aside is worked out from the pending orders (`apartados.ts`), never stored.
 */

import { ZERO, ZERO_QTY, centavos, formatQuantity, orderKey, type AnyDomainEvent, type Milli, type PayloadOf } from '@abastece/shared';
import { DONDE_SE_APARTA, apartadoPorProducto, factoresEnLibros, libre, loQuePide } from '../apartados.js';
import type { ApplyResult, Pedido, Reader } from '../state.js';

export async function reduceCliente(
  event: AnyDomainEvent,
  read: Reader,
): Promise<ApplyResult | null> {
  const rev = orderKey(event);

  switch (event.type) {
    case 'cliente.creado': {
      const p = event.payload;
      return {
        effects: [
          {
            kind: 'cliente.upsert',
            id: p.clienteId,
            row: {
              id: p.clienteId,
              nombre: p.nombre,
              tipo: p.tipo,
              telefono: p.telefono ?? null,
              direccion: p.direccion ?? null,
              listaId: p.listaId,
              diaVisita: p.diaVisita,
              notas: p.notas ?? null,
              limiteCredito: p.limiteCredito === null ? null : centavos(p.limiteCredito),
              saldo: ZERO,
              cedula: p.cedula ?? null,
              fotoCedula: p.fotoCedula ?? null,
              activo: true,
              rev,
            },
          },
        ],
        rejections: [],
      };
    }

    case 'cliente.actualizado': {
      const p = event.payload;
      if (!(await read.cliente(p.clienteId))) {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'El cliente no existe' }] };
      }
      const patch: Record<string, unknown> = {};
      for (const campo of [
        'nombre',
        'tipo',
        'telefono',
        'direccion',
        'listaId',
        'diaVisita',
        'notas',
        'limiteCredito',
        'cedula',
        'fotoCedula',
      ] as const) {
        if (p[campo] !== undefined) patch[campo] = p[campo] ?? null;
      }
      return { effects: [{ kind: 'cliente.patch', id: p.clienteId, rev, patch }], rejections: [] };
    }

    case 'cliente.desactivado':
      return {
        effects: [{ kind: 'cliente.patch', id: event.payload.clienteId, rev, patch: { activo: false } }],
        rejections: [],
      };

    // ---------- Preventa ----------

    case 'pedido.tomado': {
      const p = event.payload;

      const cliente = await read.cliente(p.clienteId);
      if (!cliente) {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'El cliente no existe' }] };
      }
      if (await read.pedido(p.pedidoId)) {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'Ese pedido ya fue tomado' }] };
      }
      const rechazo = await comprobarPedido(p, read, null);
      if (rechazo) return { effects: [], rejections: [{ eventId: event.id, reason: rechazo }] };

      /** Nothing is posted: until goods actually leave the building nothing has happened that the books should know about. */
      const pedido: Pedido = {
        id: p.pedidoId,
        clienteId: p.clienteId,
        fecha: p.fecha,
        fechaEntrega: p.fechaEntrega,
        listaId: p.listaId,
        lineas: lineasDelPedido(p.lineas, p.listaId),
        total: centavos(p.total),
        // Nothing is booked for it either: the sale that delivers the order charges the trip.
        ...(p.envio ? { envio: centavos(p.envio) } : {}),
        estado: 'pendiente',
        ventaId: null,
        motivoCancelacion: null,
        nota: p.nota ?? null,
        rev,
      };

      return { effects: [{ kind: 'pedido.upsert', id: p.pedidoId, row: pedido }], rejections: [] };
    }

    case 'pedido.actualizado': {
      const p = event.payload;
      const pedido = await read.pedido(p.pedidoId);
      if (!pedido) {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'Ese pedido no existe' }] };
      }
      if (pedido.estado !== 'pendiente') {
        return {
          effects: [],
          rejections: [
            {
              eventId: event.id,
              reason:
                pedido.estado === 'entregado'
                  ? 'Ese pedido ya fue entregado: lo que se cobró se corrige en la venta'
                  : 'Ese pedido fue cancelado',
            },
          ],
        };
      }
      if (!(await read.cliente(p.clienteId))) {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'El cliente no existe' }] };
      }
      const rechazo = await comprobarPedido(p, read, pedido);
      if (rechazo) return { effects: [], rejections: [{ eventId: event.id, reason: rechazo }] };

      // Every field it holds, as it now stands: a delivery or a note taken off is gone.
      return {
        effects: [
          {
            kind: 'pedido.patch',
            id: p.pedidoId,
            rev,
            patch: {
              clienteId: p.clienteId,
              fechaEntrega: p.fechaEntrega,
              listaId: p.listaId,
              lineas: lineasDelPedido(p.lineas, p.listaId),
              total: centavos(p.total),
              envio: centavos(p.envio),
              nota: p.nota,
            },
          },
        ],
        rejections: [],
      };
    }

    case 'pedido.cancelado': {
      const p = event.payload;
      const pedido = await read.pedido(p.pedidoId);
      if (!pedido) {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'Ese pedido no existe' }] };
      }
      if (pedido.estado === 'entregado') {
        return {
          effects: [],
          rejections: [{ eventId: event.id, reason: 'Ese pedido ya fue entregado. Anule la venta.' }],
        };
      }
      return {
        effects: [
          {
            kind: 'pedido.patch',
            id: p.pedidoId,
            rev,
            patch: { estado: 'cancelado', motivoCancelacion: p.motivo },
          },
        ],
        rejections: [],
      };
    }

    default:
      return null;
  }
}

type LineaDelEvento = PayloadOf<'pedido.tomado'>['lineas'][number];

/**
 * An order's lines as the books keep them. A line's own list only where it is not the order's
 * (`listaId`), as a sale keeps its lines'.
 */
function lineasDelPedido(lineas: readonly LineaDelEvento[], listaId: string): Pedido['lineas'] {
  return lineas.map((l) => ({
    productoId: l.productoId,
    unidadVentaId: l.unidadVentaId,
    cantidad: l.cantidad as Milli,
    precioUnitario: centavos(l.precioUnitario),
    subtotal: centavos(l.subtotal),
    ...(l.listaId && l.listaId !== listaId ? { listaId: l.listaId } : {}),
    // Kept for the till, which puts them on the sale that delivers the order. Never refused
    // here, as on a sale: «Control» is what flags a yes from somebody who is not an owner.
    ...(l.motivoOverride ? { motivoOverride: l.motivoOverride } : {}),
    ...(l.autorizadoPor ? { autorizadoPor: l.autorizadoPor } : {}),
  }));
}

/**
 * Why an order, taken or changed, cannot go on the books once its client is known: a product
 * unknown, a total that is not its lines, or more than the shop has free. Null when it can.
 *
 * With `apartarPedidos` on (2026-10-08), the order sets aside what it takes, and is refused past
 * what the shop holds less what the pending orders already set aside. Two phones taking the last
 * units: the server, putting the log in one order, takes the first and refuses the second, and
 * every device replays it the same. Off, and for every order taken before it was turned on, an
 * order is a promise that moves nothing, as it always was.
 *
 * An order changed (`antes`) sets aside nothing against itself, and is held to what is free only
 * for what it asks more of than it held: asking for less, or for the same, is never refused, even
 * where a sale has since left the shelf short of it.
 */
async function comprobarPedido(
  p: { lineas: readonly LineaDelEvento[]; total: number },
  read: Reader,
  antes: Pedido | null,
): Promise<string | null> {
  for (const l of p.lineas) {
    if (!(await read.producto(l.productoId))) return 'Un producto del pedido no existe';
  }

  // The total is what the driver will charge, in cash or on fiado; it has to be the sum of the lines.
  const suma = p.lineas.reduce((a, l) => a + l.subtotal, 0);
  if (suma !== p.total) return `El total del pedido no cuadra: líneas ${suma}, total ${p.total}`;

  if (!(await read.config()).apartarPedidos) return null;
  const pendientes = await read.pedidosPendientes();
  const factorDe = await factoresEnLibros(
    read,
    [...p.lineas, ...pendientes.flatMap((x) => x.lineas), ...(antes?.lineas ?? [])].map((l) => l.unidadVentaId),
  );
  const apartado = apartadoPorProducto(pendientes, factorDe, antes?.id ?? null);
  const tenia = antes ? loQuePide(antes.lineas, factorDe) : new Map<string, Milli>();
  for (const [productoId, pide] of loQuePide(p.lineas as Pedido['lineas'], factorDe)) {
    if (pide <= (tenia.get(productoId) ?? ZERO_QTY)) continue;
    const hay = (await read.existencia(productoId, DONDE_SE_APARTA))?.cantidad ?? ZERO_QTY;
    const queda = libre(hay, apartado.get(productoId) ?? ZERO_QTY);
    if (pide > queda) {
      const producto = await read.producto(productoId);
      return `No alcanza ${producto?.nombre ?? 'un producto'}: quedan ${formatQuantity(queda)} sin apartar y el pedido pide ${formatQuantity(pide)}`;
    }
  }
  return null;
}
